import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AiTask, Job, JobDraft } from '@etnrlz-jobs/shared';
import { api } from '../lib/api';
import { ErrorBox, PageHeader, Spinner } from '../components/ui';
import { emptyDraft, JobForm } from '../components/JobForm';

type Phase = 'briefing' | 'generating' | 'editing';

export function JobNewPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('briefing');
  const [briefing, setBriefing] = useState('');
  const [draft, setDraft] = useState<JobDraft | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const pollRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    },
    [],
  );

  const generate = async () => {
    setError('');
    setPhase('generating');
    try {
      const { taskId } = await api<{ taskId: string }>('/admin/ai/generate-job', {
        method: 'POST',
        body: { briefing },
      });
      pollRef.current = window.setInterval(() => {
        void (async () => {
          try {
            const { task } = await api<{ task: AiTask }>(`/admin/ai/tasks/${taskId}`);
            if (task.status === 'done' && task.result) {
              if (pollRef.current) window.clearInterval(pollRef.current);
              setDraft(task.result);
              setPhase('editing');
            } else if (task.status === 'error') {
              if (pollRef.current) window.clearInterval(pollRef.current);
              setError(task.error ?? 'A IA falhou ao gerar a vaga');
              setPhase('briefing');
            }
          } catch {
            // erro transitório de polling; tenta de novo no próximo tick
          }
        })();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao iniciar a geração');
      setPhase('briefing');
    }
  };

  const save = async (d: JobDraft) => {
    setSaving(true);
    setError('');
    try {
      const { job } = await api<{ job: Job }>('/admin/jobs', { method: 'POST', body: d });
      navigate(`/vagas/${job.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Nova vaga"
        subtitle={
          phase === 'editing'
            ? 'Revise o que a IA gerou, ajuste e salve'
            : 'Descreva a vaga e a IA gera escopo, perguntas e testes'
        }
      />
      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}

      {phase === 'briefing' && (
        <div className="card space-y-4">
          <div>
            <label className="label">Briefing da vaga</label>
            <textarea
              className="input"
              rows={8}
              placeholder={
                'Ex.: Preciso de um editor de vídeo para meu canal. Tem que dominar CapCut e Premiere, ' +
                'entender de retenção e storytelling, disponibilidade de 20h/semana, remoto, budget R$ 3-5k/mês PJ...'
              }
              value={briefing}
              onChange={(e) => setBriefing(e.target.value)}
            />
            <p className="mt-2 text-xs text-slate-500">
              Quanto mais contexto (função, habilidades, carga horária, budget, cultura), melhor o
              resultado. Você revisa tudo antes de publicar.
            </p>
          </div>
          <div className="flex justify-between">
            <button
              className="btn-secondary"
              onClick={() => {
                setDraft(emptyDraft);
                setPhase('editing');
              }}
            >
              Criar do zero (sem IA)
            </button>
            <button className="btn-primary" disabled={briefing.trim().length < 10} onClick={generate}>
              Gerar com IA
            </button>
          </div>
        </div>
      )}

      {phase === 'generating' && (
        <div className="card flex flex-col items-center gap-4 py-16 text-slate-300">
          <Spinner className="h-8 w-8 text-brand-400" />
          <p>A IA está estruturando a vaga, perguntas e testes…</p>
          <p className="text-xs text-slate-500">Isso costuma levar de 15 a 40 segundos.</p>
        </div>
      )}

      {phase === 'editing' && draft && (
        <JobForm initial={draft} onSave={save} saving={saving} saveLabel="Salvar vaga" />
      )}
    </div>
  );
}
